import { useCalcPrice, useComboCartItems } from "@/talons";
import { CartItems } from "./CartContext";
import { normalizeParametroPrecoBase } from "@/shared";
import { PriceProductProps, PercentualFornecedorProps, ProductProps, TaxSubstitutionProps } from "@/types";
import { useMemo, useContext, useCallback, createContext, useRef } from "react";
import { useTables, useGlobals, useCart, useDiscountFlex, useDiscountAmountFlex, useClient } from "@/hooks";
import { useSelectedClientScope } from "./useSelectedClientScope";
import { useCorporateFlags } from "./CorporateFlagsContext";
import { computeCartLineTotalWithComboDiscount, computeCheckoutLineStPricing, roundMoney } from "@/talons/combos/comboCartTotalsUtils";
import { resolveCartLineUnitPrice } from "@/talons/combos/comboCheckoutUtils";

interface CartToTalsProps {
  cartTotals: {
    flex: any;
    total: number;
    subtotal: number;
    st_price: number;
  };
  fetchCorrectPrice: (
    price: PriceProductProps[],
    option_uid: string,
    paymentCondition: number
  ) => PriceProductProps;
  fetchTotalPriceByPaymentCode: (code: number) => number;
  listaTributacao: TaxSubstitutionProps[];
}

export const CartTotalsContext = createContext({} as CartToTalsProps);

const CartTotalsProvider = ({ children }: React.PropsWithChildren) => {
  const { tables } = useTables();
  const { getPrices, findPrice, handlePrices } = useCalcPrice();
  const products = tables.current.dicionario_produtos;
  const listaTributacao = tables.current.cod_regime_tributario;

  const { currentCartDetails } = useCart();
  const { selectedClient } = useClient();
  const { paymentCode } = useGlobals();
  const { selectedBrandPair } = useCorporateFlags();
  const { getDiscountProduct } = useDiscountFlex();
  const { discountAmountClient } = useDiscountAmountFlex();
  const { combos, comboCartItemsByUid, isComboCompleteById } = useComboCartItems();
  const debugTotalsByPaymentRef = useRef<Record<number, {
    total: number;
    generatedAt: string;
    itemPrices: Record<string, number | null>;
  }>>({});

  const selectedClientScope = useSelectedClientScope();
  const shouldDebugPaymentTotals = false;

  const condicoes = useMemo(() => {
    const fromPayments = (pay: unknown) =>
      (Array.isArray(pay) ? pay : [])
        .map((item: { cod_condicao?: number; COD_CONDICAO?: number }) =>
          Number(item?.cod_condicao ?? item?.COD_CONDICAO)
        )
        .filter((value) => Number.isFinite(value) && value > 0);

    const scoped = fromPayments(selectedClientScope?.COD_PAGAMENTO);
    if (scoped.length > 0) return scoped;

    return fromPayments(selectedClient?.COD_PAGAMENTO);
  }, [selectedClientScope?.COD_PAGAMENTO, selectedClient?.COD_PAGAMENTO]);

  const comboPriceCalcParams = useMemo(() => {
    const codLoja = selectedClientScope?.COD_LOJA;
    const tabVenda = selectedClientScope?.COD_TABELA_VENDA;
    const percentual = (tables.current.percentual_fornecedor as PercentualFornecedorProps[]) ?? [];
    const listSubstTrib = (listaTributacao as TaxSubstitutionProps[]) ?? [];
    const parametroPrecoPromocao = tables.current.precoPromocaoCliente;
    const percentualFornecedor = (Array.isArray(percentual) ? percentual : []).filter(
      (entry) => `${entry.NROTABVENDA}` === `${tabVenda}`
    );
    const parametroPreco = normalizeParametroPrecoBase(
      tables.current.parametro_preco_base,
      codLoja,
      tabVenda,
      condicoes
    );

    return {
      parametroPreco,
      percentualFornecedor,
      listSubstTrib,
      parametroPrecoPromocao,
    };
  }, [
    selectedClientScope?.COD_LOJA,
    selectedClientScope?.COD_TABELA_VENDA,
    tables,
    condicoes,
    listaTributacao,
  ]);

  const comboLinePriceDeps = useMemo(
    () => ({
      combos,
      isComboCompleteById,
      condicoes,
      handlePrices,
      findPrice,
      priceCalcParams: comboPriceCalcParams,
    }),
    [combos, isComboCompleteById, condicoes, handlePrices, findPrice, comboPriceCalcParams]
  );

  const cartHasComboAllocation = useMemo(
    () => combos.length > 0 && Object.values(comboCartItemsByUid).some((allocations) => allocations?.length),
    [combos.length, comboCartItemsByUid]
  );

  const fetchCorrectPrice = useCallback(
    (
      price: PriceProductProps[],
      option_uid: string,
      paymentCondition: number
    ) => {
      return findPrice(price, option_uid, paymentCondition);
    },
    [findPrice]
  );

  const fetchTotalPriceByPaymentCode = useCallback(
    (code: number) => {
      if (!selectedClientScope) return 0;
      if (
        currentCartDetails?.items?.length > 0 &&
        code
      ) {
        const debugItemPricesByKey = {} as Record<string, number | null>;
        if (shouldDebugPaymentTotals) {
          console.log('[CartTotalsContext][cart-total-debug][start]', {
            paymentConditionCode: code,
            selectedClientScope,
            selectedBrandPair,
            paymentCodeGlobal: paymentCode,
            cartDetailsKeyInfo: {
              itemsInCart: currentCartDetails?.items?.length || 0,
              items: currentCartDetails?.items || [],
            },
          });
        }

        const totals = currentCartDetails?.items?.reduce((acc: number, item: CartItems) => {
          if (!products?.[item?.sku]) return acc;
          const prices = getPrices(products?.[item?.sku]);
          
          if (!!prices) {
            const finalprice = resolveCartLineUnitPrice(
              item,
              prices,
              code,
              findPrice,
              getDiscountProduct,
              selectedClientScope,
              listaTributacao
            );

            if (finalprice) {
              const allocations = comboCartItemsByUid[item.uid];
              const fullprice = roundMoney(
                cartHasComboAllocation && allocations?.length
                  ? computeCartLineTotalWithComboDiscount(
                      item,
                      finalprice,
                      allocations,
                      products[item.sku] as ProductProps,
                      code,
                      comboLinePriceDeps
                    )
                  : finalprice * item?.quantity
              );
              if (shouldDebugPaymentTotals) {
                const [packing, qtd] = item?.option_uid?.split("_") || [];
                const price = findPrice(prices, item?.option_uid, code);
                const candidatePricesForCondition = prices.filter(
                  (p) => Number(p?.NROCONDICAOPAGTO) === Number(code)
                );
                console.log('[CartTotalsContext][cart-total-debug][item]', {
                  paymentConditionCode: code,
                  item,
                  selectedClientScope,
                  optionUidParsed: { packing, qtd },
                  productFromDictionary: products?.[item?.sku],
                  pricesInputLength: prices?.length || 0,
                  pricesInput: prices,
                  candidatePricesForConditionLength: candidatePricesForCondition.length,
                  candidatePricesForCondition,
                  matchedPrice: price,
                  finalprice,
                  fullprice,
                  runningTotalBeforeAdd: acc,
                  runningTotalAfterAdd: acc + fullprice,
                });
              }
              debugItemPricesByKey[`${item?.sku}-${item?.option_uid}`] = finalprice;
              if (fullprice) return (acc += fullprice);
            }

            if (shouldDebugPaymentTotals) {
              const [packing, qtd] = item?.option_uid?.split("_") || [];
              const price = findPrice(prices, item?.option_uid, code);
              const candidatePricesForCondition = prices.filter(
                (p) => Number(p?.NROCONDICAOPAGTO) === Number(code)
              );

              if (!candidatePricesForCondition.length) {
                console.warn('[CartTotalsContext][cart-total-debug][warning-no-candidates-for-condition]', {
                  paymentConditionCode: code,
                  item,
                  optionUidParsed: { packing, qtd },
                  selectedClientScope,
                });
              }

              if (!price?.NROCONDICAOPAGTO || Number(price?.NROCONDICAOPAGTO) !== Number(code)) {
                console.warn('[CartTotalsContext][cart-total-debug][warning-no-matched-price]', {
                  paymentConditionCode: code,
                  item,
                  optionUidParsed: { packing, qtd },
                  candidatePricesForConditionLength: candidatePricesForCondition.length,
                  selectedClientScope,
                });
              }

              console.log('[CartTotalsContext][cart-total-debug][item-no-add]', {
                paymentConditionCode: code,
                item,
                selectedClientScope,
                pricesInputLength: prices?.length || 0,
                pricesInput: prices,
                candidatePricesForConditionLength: candidatePricesForCondition.length,
                candidatePricesForCondition,
                matchedPrice: price,
                finalprice,
                fullpriceComputed: finalprice ? finalprice * item?.quantity : null,
              });
            }
            debugItemPricesByKey[`${item?.sku}-${item?.option_uid}`] = finalprice ?? null;
          }
          return acc;
        }, 0);

        if (shouldDebugPaymentTotals) {
          debugTotalsByPaymentRef.current[code] = {
            total: totals || 0,
            generatedAt: new Date().toISOString(),
            itemPrices: debugItemPricesByKey,
          };

          const paymentTotalsSnapshot = Object.entries(debugTotalsByPaymentRef.current).map(([paymentCond, payload]) => ({
            paymentConditionCode: Number(paymentCond),
            total: payload.total,
            generatedAt: payload.generatedAt,
            itemPrices: payload.itemPrices,
          }));
          const totalSet = new Set(paymentTotalsSnapshot.map((entry) => Number(entry.total || 0).toFixed(2)));
          const allTotalsEqual = paymentTotalsSnapshot.length > 1 && totalSet.size === 1;

          if (allTotalsEqual) {
            console.warn('[CartTotalsContext][cart-total-debug][warning-all-payment-totals-equal]', {
              codCliente: selectedClientScope?.COD_CLIENTE,
              codLoja: selectedClientScope?.COD_LOJA,
              codSegmentos:
                selectedClientScope?.COD_SEGMENTO?.map((s: any) => s?.cod_segmento ?? s?.COD_SEGMENTO ?? s) || [],
              codRegimeTribCliente: selectedClientScope?.COD_REGIME_TRIB,
              paymentTotalsSnapshot,
            });
          }
        }

        if (shouldDebugPaymentTotals) {
          console.log('[CartTotalsContext][cart-total-debug][end]', {
            codCliente: selectedClientScope?.COD_CLIENTE,
            codLoja: selectedClientScope?.COD_LOJA,
            codSegmentos:
              selectedClientScope?.COD_SEGMENTO?.map((s: any) => s?.cod_segmento ?? s?.COD_SEGMENTO ?? s) || [],
            codRegimeTribCliente: selectedClientScope?.COD_REGIME_TRIB,
            paymentConditionCode: code,
            itemsInCart: currentCartDetails?.items?.length || 0,
            total: totals,
          });
        }
        return totals;
      }
      return 0;
    },
    [
      currentCartDetails,
      products,
      getDiscountProduct,
      selectedClientScope,
      listaTributacao,
      getPrices,
      findPrice,
      comboCartItemsByUid,
      comboLinePriceDeps,
      cartHasComboAllocation,
    ]
  );

  const handleCalcStTotals = useCallback((code: number) => {
    if (!selectedClientScope) return 0;
    if (currentCartDetails?.items?.length > 0 && code) {
      const stTotals = currentCartDetails?.items?.reduce((acc: number, item: CartItems) => {
        if (!products?.[item?.sku]) return acc;

        const [packing, qtd] = item?.option_uid?.split("_") || [];
        const packQty = Number(qtd) || 1;
        const percentualFlex = Number(
          getDiscountProduct({
            package: packing,
            productId: item.sku,
            quantity: packQty,
            uid: item.uid,
          })?.percentual ?? 0
        );

        const catalogProduct = products[item.sku] as ProductProps;
        const prices = getPrices(catalogProduct);
        const productWithPrices =
          prices.length > 0 ? { ...catalogProduct, PRECO: prices } : catalogProduct;

        const stPricing = computeCheckoutLineStPricing(
          item,
          comboCartItemsByUid[item.uid],
          productWithPrices,
          code,
          comboLinePriceDeps,
          selectedClient,
          listaTributacao as TaxSubstitutionProps[],
          percentualFlex
        );

        return acc + (stPricing.stLineTotal || 0);
      }, 0);
      return stTotals;
    }
    return 0;
  }, [
    currentCartDetails,
    products,
    listaTributacao,
    selectedClientScope,
    selectedClient,
    comboCartItemsByUid,
    comboLinePriceDeps,
    getDiscountProduct,
    getPrices,
  ]);

  const cartTotals = useMemo(() => {
    const total = fetchTotalPriceByPaymentCode(paymentCode);
    const stTotals = handleCalcStTotals(paymentCode);
    return {
      total,
      subtotal: total - stTotals,
      st_price: stTotals,
      flex: discountAmountClient || 0,
    }
  }, [paymentCode, fetchTotalPriceByPaymentCode, handleCalcStTotals, discountAmountClient]);

  const contextValue = useMemo(
    () => ({
      cartTotals,
      fetchCorrectPrice,
      fetchTotalPriceByPaymentCode,
      listaTributacao,
    }),
    [cartTotals, fetchCorrectPrice, fetchTotalPriceByPaymentCode, listaTributacao]
  );

  return (
    <CartTotalsContext.Provider value={contextValue}>
      {children}
    </CartTotalsContext.Provider>
  );
};

const useCartTotals = () => {
  const context = useContext(CartTotalsContext);

  if (!context) {
    throw new Error(
      "o context useCartTotals só pode ser utilizado dentro de seu escopo. Erro ao usar hook useCartTotals"
    );
  }
  return context;
};

export { CartTotalsProvider, useCartTotals };
